import {
  addDays,
  differenceInMilliseconds,
  format,
  intervalToDuration,
  isBefore,
  minutesToMilliseconds,
  parse,
  sub,
} from 'date-fns'
import { IBusinessEntity } from 'models/BusinessEntity'
import { TypeOrNull } from 'models/Common'
import moment from 'moment'
import { useCallback, useEffect, useState } from 'react'
import { DeepPartial } from 'redux'
import { FEATURE_TOGGLE_MULTIDAY_REVAMP } from 'config/featureToggles'
import { SHIFT_TYPE } from 'models/Shift'
import { hasBusinessAAbyOnboardedSettingEnabled } from 'helpers/businessEntityHelpers'
import {
  IModalEditShiftValues,
  TDay,
  TEditDuplicateShiftValues,
  TShiftLengthCheckerError,
  TSingleDayShift,
  TWeeklyType,
} from './types'

export const howManyDirtyDays = (days: TDay[], dayOfWeek: number) => {
  let dirtyCount = 0

  days.forEach((day) => {
    const isRequestedDay = moment(day.day_start).isoWeekday() === dayOfWeek + 1
    if (day.is_dirty && isRequestedDay) dirtyCount++
  })

  return dirtyCount
}

export const dateIdMap = (days: TDay[]) => {
  const map = days.reduce(
    (current_map, current_day) => ({
      ...current_map,
      [current_day.date_start]: current_day,
    }),
    {}
  )

  return map
}

// TODO remove
export const extractFirstAndLastDate = (days: any[]) => {
  return days.length > 0 ? [days[0].date_start, days[days.length - 1].date_start] : []
}

/**
 * Updates days in an array with new info for every day
 *
 * @param daysArray Multiday days or Individual days array from Formik
 * @param customizer
 * @param enforceUpdate Update all days not taking into account whether they were edited or not
 */
export const updateDays = <T extends Partial<TDay> = {}>(
  daysArray: TDay[],
  customizer: ((day: TDay, index: number) => T) | T,
  dirtyUpdate?: boolean,
  enforceUpdate?: boolean
) => {
  let newDaysArray = [] as TDay[]

  if (dirtyUpdate) {
    newDaysArray = daysArray.map((day, index) => {
      if (Number(day.punch_cards_count) > 0) return day
      return {
        ...day,
        ...(!day.is_dirty || enforceUpdate
          ? typeof customizer === 'function'
            ? customizer(day, index)
            : customizer
          : undefined),
      }
    })
  } else {
    newDaysArray = daysArray.map((day, index) => {
      if (Number(day.punch_cards_count) > 0) return day
      return {
        ...day,
        ...(typeof customizer === 'function' ? customizer(day, index) : customizer),
      }
    })
  }

  return newDaysArray
}

/**
 * Generates day objects between two dates for multiday shifts that are not to hire
 *
 * @param startDay Start day of the range
 * @param endDay End day of the range
 */
export const generateMultidayDays = (
  startDay: any,
  endDay: any,
  values: TEditDuplicateShiftValues
) => {
  const numberOfDays = moment(endDay).diff(moment(startDay), 'days')
  const days = []

  /**
   * We generate the days in the inclusive range [startDay, endDay]
   */
  for (let i = 0; i <= numberOfDays; i++) {
    const currentDayStart = moment(startDay).clone().add(i, 'days')
    const currentDayEnd = getFinalDay(currentDayStart, values.hour_start, values.hour_end)

    const date_start = currentDayStart.format('MM/DD/YYYY')
    const date_end = currentDayEnd.format('MM/DD/YYYY')

    const { date_ids } = values as IModalEditShiftValues

    let id = i
    let working = true
    let punch_cards_count = 0
    let existing = false

    if (date_ids[date_start]) {
      id = date_ids[date_start].id
      working = date_ids[date_start].working
      punch_cards_count = date_ids[date_start].punch_cards_count
      existing = true
    }

    const day: TDay = {
      id,
      day_start: currentDayStart,
      day_end: currentDayEnd,
      date_start,
      date_end,
      hour_start: values.hour_start,
      hour_end: values.hour_end,
      break: values.break,
      pay_value: values.pay_value,
      working,
      punch_cards_count,
      existing,
      starting_day: false,
    }

    days.push(day)
  }

  return days
}

/**
 * Generates individual days array
 *
 * @param values Values from Formik
 * @param partialValues Partial object from Values
 */
export const generateIndividualDays = <T extends DeepPartial<TEditDuplicateShiftValues> = {}>(
  values: TEditDuplicateShiftValues,
  partialValues: T
) => {
  const {
    weekly: v_weekly,
    number_of_weeks: v_number_of_weeks,
    individual_day_start: v_individual_day_start,
  } = values
  const {
    weekly: pv_weekly,
    number_of_weeks: pv_number_of_weeks,
    individual_day_start: pv_individual_day_start,
  } = partialValues

  const weekly = { ...v_weekly, ...pv_weekly }
  const number_of_weeks =
    pv_number_of_weeks !== 0 ? pv_number_of_weeks || v_number_of_weeks : pv_number_of_weeks
  const individual_day_start = (pv_individual_day_start || v_individual_day_start) as Date

  const startDay = moment(individual_day_start)
  const endDay = startDay.clone().add(number_of_weeks * 7, 'days')

  const numberOfDays = moment(endDay).diff(startDay, 'days')
  const startDayIndex = startDay.isoWeekday()

  const days = []

  /**
   * We generate the days in the inclusive range [startDay, endDay]
   */
  for (let i = 0; i < numberOfDays; i++) {
    const { date_ids } = values as IModalEditShiftValues

    const currentDayStart = moment(startDay).clone().add(i, 'days')
    const currentDayEnd = getFinalDay(currentDayStart, values.hour_start, values.hour_end)

    const date_start = currentDayStart.format('MM/DD/YYYY')
    const date_end = currentDayEnd.format('MM/DD/YYYY')

    let id = i
    let working = weekly[((startDayIndex - 1 + (i % 7)) % 7) as TWeeklyType]
    let punch_cards_count = 0
    let existing = false
    let is_dirty = false

    if (date_ids[date_start]) {
      id = date_ids[date_start].id
      working = date_ids[date_start].working
      punch_cards_count = date_ids[date_start].punch_cards_count
      existing = true
      is_dirty = true
    }

    const day: TDay = {
      id,
      day_start: currentDayStart,
      day_end: currentDayEnd,
      date_start,
      date_end,
      hour_start: values.hour_start,
      hour_end: values.hour_end,
      break: values.break,
      pay_value: values.pay_value,
      working,
      punch_cards_count,
      existing,
      is_dirty,
      starting_day: false,
    }

    days.push(day)
  }

  return days
}

const extractPeriod = (time: string) => {
  if (time.includes('AM')) {
    return 'AM'
  } else if (time.includes('PM')) {
    return 'PM'
  } else return null
}

const extractHourAndMinute = (time: string) => {
  const [hour, remainder] = time.split(':')
  const other = remainder || ''
  const [minute] = other.split(' ')

  return [hour, minute]
}

const isStartBeforeEnd = (time_start: string, time_end: string) => {
  const [hour_start, minute_start] = extractHourAndMinute(time_start)
  const [hour_end, minute_end] = extractHourAndMinute(time_end)

  if (Number(hour_start) === 12) {
    // We know that times have the same period
    return true
  }
  if (Number(hour_start) > Number(hour_end)) return false
  if (Number(hour_start) === Number(hour_end)) {
    if (Number(minute_start) > Number(minute_end)) return false
    else return true
  }

  return true
}

export const areTimesInTheSameDay = (time_start: string, time_end: string) => {
  const startPeriod = extractPeriod(time_start)
  const endPeriod = extractPeriod(time_end)

  if (startPeriod === endPeriod) {
    if (isStartBeforeEnd(time_start, time_end)) return true
    else return false
  } else if (startPeriod === 'PM' && endPeriod === 'AM') return false

  return true
}

export const getFinalDay = (day_start: moment.Moment, hour_start: string, hour_end: string) => {
  let dayStartMoment = day_start
  if (!moment.isMoment(day_start)) {
    dayStartMoment = moment(day_start)
  }
  return areTimesInTheSameDay(hour_start, hour_end)
    ? dayStartMoment.clone()
    : dayStartMoment.clone().add(1, 'days')
}

export const correctEndDate = (day_end: moment.Moment, hour_start: string, hour_end: string) => {
  let dayEndMoment = day_end
  if (!moment.isMoment(day_end)) {
    dayEndMoment = moment(day_end)
  }
  return areTimesInTheSameDay(hour_start, hour_end)
    ? dayEndMoment.clone().format('MM/DD/YYYY')
    : dayEndMoment.clone().subtract(1, 'days').format('MM/DD/YYYY')
}

/**
 * A util function for calculating when the shift will end
 * @param {*} dateStart - string
 * @param {*} hourStart - string
 * @param {*} hourEnd - string
 */
export const calculateEndDateFomStartDateAndHours = (
  dateStart: string,
  hourStart: string,
  hourEnd: string
) => {
  let dateEnd = dateStart
  const beginningTime = parse(hourStart, 'hh:mm a', new Date())
  const endTime = parse(hourEnd, 'hh:mm a', new Date())

  // We're checking if the end time is before the beginning time
  // If it is, we're adding a day to the dateEnd
  if (isBefore(endTime, beginningTime)) {
    dateEnd = format(addDays(new Date(dateStart), 1), 'MM/dd/yyyy')
  }

  return dateEnd
}

interface ICalculateShiftAmountProps {
  startTime: string
  endTime: string
  rate: string
  unpaidBreak: number
}

interface ICalculateUpshiftFeeProps {
  commissionPercent: number
  totalShiftAmount: number
}

interface ICalculateShiftTotalProps {
  totalShiftAmount: number
  upshiftFee: number
}

export const parseCalculation = (value: number) => {
  return parseFloat(value.toString()).toFixed(2)
}

export const calculateShiftAmount = ({
  startTime,
  endTime,
  rate,
  unpaidBreak,
}: ICalculateShiftAmountProps) => {
  // We're initializing the date as today's date since it doesn't make a difference in the calculations
  // it's only used to see if the shift will end in a new day (after 12AM)
  const dateStart = format(new Date(), 'MM/dd/yyyy')
  // Calculate the end date of the shift
  const dateEnd = calculateEndDateFomStartDateAndHours(dateStart, startTime, endTime)
  // Parse the end date time to a hh:mm a format
  const timeDateEnd = parse(endTime, 'hh:mm a', new Date(dateEnd))
  // Parse the start date time to a hh:mm a format
  const timeDateStart = parse(startTime, 'hh:mm a', new Date(dateStart))
  // Convert the break from minutes to milliseconds
  const breakInMilliseconds = minutesToMilliseconds(unpaidBreak)
  // Convert the break into a duration object
  const breakDuration = intervalToDuration({ start: 0, end: breakInMilliseconds })
  // Create a duration object for the shift
  const shiftDuration = intervalToDuration({
    start: 0,
    end: sub(differenceInMilliseconds(timeDateEnd, timeDateStart), breakDuration),
  })
  // Get the hours from the shift duration object
  const hourDifference = shiftDuration.hours || 0
  // Divide the minute difference from the duration by an hour (60 minutes)
  const minutesDifference = (shiftDuration.minutes || 0) / 60
  // Add the hour and minutes difference in a float variable
  const timeDifference = hourDifference + minutesDifference

  // Multiply the shift duration by the pay rate
  const totalShiftAmount = timeDifference * Number(rate)

  return totalShiftAmount
}

export const calculateUpshiftFee = ({
  commissionPercent,
  totalShiftAmount,
}: ICalculateUpshiftFeeProps) => {
  const upshiftFee = totalShiftAmount * commissionPercent

  return upshiftFee
}

export const calculateShiftTotal = ({
  totalShiftAmount,
  upshiftFee,
}: ICalculateShiftTotalProps) => {
  const shiftTotal = totalShiftAmount + upshiftFee
  return shiftTotal
}

/**
 *
 * @param day
 */
// TODO add stats.punchcards_count so we can disable day editing if the particular day has at least one punchcard
const dayMapper = (weekly: { [key in TWeeklyType]: boolean }) => (day: any) => {
  const {
    id,
    date_start,
    date_end,
    hour_start,
    hour_end,
    pay_value,
    break: unpaid_break,
    working_day: working,
    punch_cards_count,
    starting_day,
  } = day

  const dayIndex = (moment(date_start).isoWeekday() - 1) as TWeeklyType
  const isDayDirty = weekly[dayIndex] !== working

  const mappedDay: TDay = {
    id,
    day_start: moment(date_start),
    day_end: moment(date_end),
    date_start,
    date_end,
    hour_start,
    hour_end,
    pay_value,
    break: unpaid_break,
    working,
    punch_cards_count,
    existing: true,
    is_dirty: isDayDirty,
    starting_day,
  }

  return mappedDay
}

export const mapDays = (days: any[], weekly: { [key in TWeeklyType]: boolean }) => {
  return days.map(dayMapper(weekly))
}

export const mapWeeklyOn = (weekly: any) => {
  return (weekly || []).reduce(
    (currentObject: any, currentDay: any) => ({ ...currentObject, [currentDay - 1]: true }),
    {
      0: false,
      1: false,
      2: false,
      3: false,
      4: false,
      5: false,
      6: false,
    }
  )
}

/**
 *
 * Returns the dropdown values ready for Select
 * @param {Boolean} dropdownReady
 */
export const getBreakValue = (dropdownReady = false) => {
  const params = [
    {
      value: 0,
      label: 'No Break',
      action: 'No Break',
    },
    {
      value: 30,
      label: '30 min',
      action: 30,
    },
    {
      value: 45,
      label: '45 min',
      action: 45,
    },
    {
      value: 60,
      label: '60 min',
      action: 60,
    },
    {
      value: 75,
      label: '75 min',
      action: 75,
    },
  ]
  return params
}

/**
 * If the business entity has disabled some auto accept feature for them,
 * reset the fields before posting the shift.
 * (fields may be lingering from the shift we are duplicating).
 * @param shift Can be an array of single day shift(s) or an object
 */
export const handleSelectiveClaimFields = (shift: any, businessEntity: IBusinessEntity) => {
  const scmFields: any = {}
  if (!businessEntity.auto_accept_by_favored) {
    scmFields.auto_accept_by_favored = false
  }
  if (!businessEntity.auto_accept_by_worked_before) {
    scmFields.auto_accept_by_worked_before = false
  }
  if (!hasBusinessAAbyOnboardedSettingEnabled(businessEntity)) {
    scmFields.auto_accept_by_onboarded = false
  }
  if (!businessEntity.auto_accept_by_certificates) {
    scmFields.auto_accept_by_certificates = false
    scmFields.auto_accept_certificate_ids = []
  }

  if (Array.isArray(shift)) {
    const newShifts = shift.map((singleShift) => {
      return {
        ...singleShift,
        ...scmFields,
      }
    })
    return newShifts
  } else {
    const newShift = {
      ...shift,
      ...scmFields,
    }
    return newShift
  }
}

export const calculateShiftDurationHours = (
  dateStart: string,
  hourStart: string,
  hourEnd: string
) => {
  const endDate = calculateEndDateFomStartDateAndHours(dateStart, hourStart, hourEnd)

  const endDateTime = moment(`${endDate}  ${hourEnd}`)
  const startDateTime = moment(`${dateStart}  ${hourStart}`)
  const duration = moment.duration(endDateTime.diff(startDateTime))
  const shiftHoursLength = duration.asHours()

  return shiftHoursLength
}

/**
 * Function that checks if the Shift's length is valid
 * @param shift Array of single day shifts or a single multiday shift pbject
 */
export const shiftLengthChecker = (shift: any): TypeOrNull<TShiftLengthCheckerError> => {
  let shiftLengthError: TypeOrNull<TShiftLengthCheckerError> = null
  // When the shift is Single Day, the shift param will be an array (it can be of multiple shift objects, but they all have the same start and end time)
  if (Array.isArray(shift)) {
    if (Number(shift[0].type) === SHIFT_TYPE.SINGLEDAY) {
      for (let i = 0; i < shift.length; i++) {
        const { date_start, hour_start, hour_end } = shift[i]
        const shiftHoursLength = calculateShiftDurationHours(
          date_start.toString(),
          hour_start,
          hour_end
        )

        if (shiftHoursLength > 15) {
          shiftLengthError = {
            message: 'Cannot post shift over 15 hours',
            day_id: shift[i].date_start,
          }
          break
        }

        if (shiftHoursLength === 0) {
          shiftLengthError = {
            message: 'Cannot post shift with the same Start and End Time',
            day_id: shift[i].date_start,
          }
          break
        }
      }
    }
  } else if (Number(shift.type) === SHIFT_TYPE.MULTIDAY) {
    const shiftDays = shift.days

    for (let i = 0; i < shiftDays.length; i++) {
      const { date_start, hour_start, hour_end, id } = shiftDays[i]
      const dayHoursLength = calculateShiftDurationHours(date_start, hour_start, hour_end)

      if (dayHoursLength > 15) {
        shiftLengthError = { message: 'Cannot post shift over 15 hours', day_id: id }
        break
      }

      if (dayHoursLength === 0) {
        shiftLengthError = {
          message: 'Cannot post shift with the same Start and End Time',
          day_id: id,
        }
        break
      }
    }
  }

  return shiftLengthError
}

/**
 * Updates all of the Single day shifts only one field at a time.
 * @param shifts
 * @param field
 * @param value
 * @returns Single day shifts
 */
export const updateSingleDayShifts = (
  shifts: TSingleDayShift[],
  field: string,
  value: string | number
) => {
  if (!shifts) {
    return []
  }

  return shifts.map((shift) => ({
    ...shift,
    [field]: value,
  }))
}

/**
 * Updates specific Single day shift by ID.
 * @param shifts
 * @param id
 * @param shift
 * @returns
 */
export const updateSingleDayShift = (
  shifts: TSingleDayShift[],
  id: string,
  shift: Partial<TSingleDayShift>
) => {
  const shiftIndex = shifts.findIndex((shift) => shift.id === id)
  const newShifts = [...shifts]
  newShifts[shiftIndex] = {
    ...shifts[shiftIndex],
    ...shift,
  }
  return newShifts
}

/**
 * Updates a specific day in the days array and returns a new array. (multiday)
 * @param days Days array in formik
 * @param id Id of the specific day in the array
 * @param day Partial object from the new day
 */
export const updateSpecificDay = (days: TDay[], id: number, day: Partial<TDay>) => {
  const dayIndex = days.findIndex((day) => day.id === id)
  const newDays = [...days]

  newDays[dayIndex] = {
    ...days[dayIndex],
    ...day,
  }

  return newDays
}

/**
 * Removes a Single day shift by ID.
 * @param shifts
 * @param id
 * @returns
 */
export const deleteSingleDayShift = (shifts: TSingleDayShift[], id: string) =>
  shifts.filter((shift) => shift.id !== id)

export type TPaginatedSingleDayShifts = { [key: number]: TSingleDayShift[] }
/**
 * Hook that transforms a Single day shifts array into a paginated object, exposing it together with additional page fields.
 * Make sure to check if the paginatedShifts object is empty before passing it as a prop to the Table component.
 * It will be empty on the first render and the Table component will crash, receiving undefined value for data.
 * @param shifts Single Day Shifts
 * @returns
 */
export const usePaginatedSingleDayShifts = (shifts: TSingleDayShift[]) => {
  const [paginatedShifts, setPaginatedShifts] = useState<TPaginatedSingleDayShifts>(
    {} as TPaginatedSingleDayShifts
  )
  const [currentPage, setCurrentPage] = useState(1)
  const [lastPage, setLastPage] = useState(1)
  const mapShiftsPerPage = useCallback(() => {
    let numberOfPages = 1
    if (shifts.length % 7) {
      numberOfPages = Math.floor(shifts.length / 7) + 1
    } else {
      numberOfPages = shifts.length / 7
    }

    setLastPage(numberOfPages)
    setCurrentPage(currentPage > numberOfPages ? numberOfPages : currentPage)
    let currentFillingPage = 1
    const preparedShifts: {
      [key: number]: TSingleDayShift[]
    } = {}
    for (let i = 0; i < shifts.length; i++) {
      if (i > 0 && i % 7 === 0 && currentFillingPage < numberOfPages) {
        currentFillingPage += 1
      }

      const currentFillingPageExists = !!preparedShifts[currentFillingPage]
      preparedShifts[currentFillingPage] = currentFillingPageExists
        ? [...preparedShifts[currentFillingPage], shifts[i]].sort(
            (a: TSingleDayShift, b: TSingleDayShift) => {
              return a.date_start.getTime() - b.date_start.getTime()
            }
          )
        : [shifts[i]].sort((a: TSingleDayShift, b: TSingleDayShift) => {
            return a.date_start.getTime() - b.date_start.getTime()
          })
    }
    setPaginatedShifts(preparedShifts)
  }, [currentPage, shifts])

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page)
  }, [])

  useEffect(() => {
    mapShiftsPerPage()
  }, [shifts, mapShiftsPerPage])

  return {
    paginatedShifts,
    currentPage,
    lastPage,
    handlePageChange,
  }
}

/**
 * Function for checking whether the business requires next working day selection
 * i.e. Manual Posting setting is enabled for the business
 */
export const checkIfBusinessRequiresNextWorkingDaySelection = (businessEntity: IBusinessEntity) => {
  return (
    FEATURE_TOGGLE_MULTIDAY_REVAMP &&
    businessEntity?.business_setting?.requires_next_working_day_selection
  )
}
